/**
 * C0-LOOK P2: the sky and cloud sea under the floating diorama. The old sky (terrain.ts `paintSky`)
 * was a dusk gradient that ended in near-black, so at the edge of the map the world dropped into a
 * flat navy void. This one is a hue-shifted gradient in dithered steps (high blue-violet sky, a peach
 * horizon with a pale low sun, then a lavender cloud sea sinking to indigo), three ridges rising out
 * of the clouds with a lit rim and mist at their feet, and dithered cloud banks. Drawn once per
 * size; the time-of-day grade is applied over it by the lighting passes as before.
 */
import { canvas, ctx2d } from './pixel';

const BAY = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
const bay = (x: number, y: number) => BAY[(y & 3) * 4 + (x & 3)]!;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

function hash(x: number, s: number): number {
  let h = (x * 374761393 + s * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function noise1(x: number, s: number): number {
  const xi = Math.floor(x), f = x - xi, u = f * f * (3 - 2 * f);
  return lerp(hash(xi, s), hash(xi + 1, s), u);
}
function noise2(x: number, y: number, s: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  const h = (a: number, b: number) => hash(a * 7919 + b, s);
  return lerp(lerp(h(xi, yi), h(xi + 1, yi), u), lerp(h(xi, yi + 1), h(xi + 1, yi + 1), u), v);
}

type RGB = [number, number, number];
const STOPS: [number, RGB][] = [
  [0, [62, 74, 128]],
  [0.3, [118, 112, 166]],
  [0.5, [214, 170, 168]],
  [0.58, [236, 196, 170]],
  [0.64, [190, 160, 190]],
  [0.82, [112, 98, 152]],
  [1, [58, 54, 100]],
];
function ramp(t: number): RGB {
  for (let i = 1; i < STOPS.length; i++) {
    const [t1, c1] = STOPS[i]!;
    if (t <= t1) {
      const [t0, c0] = STOPS[i - 1]!;
      const k = (t - t0) / (t1 - t0);
      return [lerp(c0[0], c1[0], k), lerp(c0[1], c1[1], k), lerp(c0[2], c1[2], k)];
    }
  }
  return STOPS[STOPS.length - 1]![1];
}

/** The sky picture, `W` x `H` art pixels. */
export function paintSkyDiorama(W: number, H: number): HTMLCanvasElement {
  const c = canvas(W, H);
  const g = ctx2d(c);
  const id = g.createImageData(W, H);
  const d = id.data;
  const N = 14; // gradient steps
  const sunX = W * 0.68, sunY = H * 0.52;
  const ridges = [
    { base: 0.58, amp: 0.12, col: [140, 132, 176] as RGB, rim: [196, 170, 190] as RGB, seed: 1, f: 70 },
    { base: 0.7, amp: 0.14, col: [96, 88, 142] as RGB, rim: [150, 128, 168] as RGB, seed: 2, f: 55 },
    { base: 0.84, amp: 0.12, col: [60, 54, 104] as RGB, rim: [104, 88, 138] as RGB, seed: 3, f: 42 },
  ];
  const tops = ridges.map((r) => {
    const a = new Int16Array(W);
    for (let x = 0; x < W; x++)
      a[x] = Math.round(H * (r.base - r.amp * (0.7 * noise1(x / r.f + r.seed * 13, r.seed) + 0.3 * noise1(x / 17 + r.seed * 7, r.seed + 3))));
    return a;
  });
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const t = y / H;
      const L = t * N;
      let col = ramp((Math.floor(L) + (L % 1 > bay(x, y) ? 1 : 0)) / N);
      // The low sun: three dithered rings of brightening.
      const sd = Math.hypot((x - sunX) / 1.6, y - sunY) / (H * 0.2);
      const sv = clamp(1 - sd, 0, 1) * 3;
      const ss = Math.floor(sv) + (sv % 1 > bay(x, y) ? 1 : 0);
      if (ss > 0) col = [lerp(col[0], 255, 0.2 * ss), lerp(col[1], 238, 0.2 * ss), lerp(col[2], 205, 0.18 * ss)];
      // Cloud banks in the sea (below the horizon): lit tops, shaded undersides.
      if (t > 0.6) {
        const m = (yy: number) => noise2(x / 46, yy / 9, 5) > 0.64 + (1 - t) * 0.1;
        if (m(y)) col = m(y - 3) ? [168, 148, 190] : [226, 206, 220];
      }
      // The ridges, far to near; mist pools at each foot.
      for (let k = 0; k < ridges.length; k++) {
        const r = ridges[k]!, top = tops[k]![x]!;
        if (y >= top) {
          col = y < top + 2 ? r.rim : r.col;
          const sh = clamp((y - top) / (H * 0.1), 0, 1); // deeper toward the bottom
          col = [col[0] * (1 - 0.18 * sh), col[1] * (1 - 0.18 * sh), col[2] * (1 - 0.1 * sh)];
        } else if (y > top - H * 0.025 && (y - (top - H * 0.025)) / (H * 0.025) > bay(x, y)) {
          // mist just above the ridge line
          col = [lerp(col[0], 232, 0.3), lerp(col[1], 214, 0.3), lerp(col[2], 226, 0.3)];
        }
      }
      const i = (y * W + x) * 4;
      d[i] = col[0];
      d[i + 1] = col[1];
      d[i + 2] = col[2];
      d[i + 3] = 255;
    }
  g.putImageData(id, 0, 0);
  return c;
}
